import {assert} from 'Asserts';
import {ExperimentProcedure} from 'studies/typed/shared/ExperimentalInputs';
import {UnitOpLinkEdge} from 'studies/typed/shared/unit-op-linking-flowchart/UnitOpLinkingFlowchartUtils';
import {UnitOperationFields} from 'studies/typed/shared/unit-op-linking-flowchart/useUnitOpLinkingState';
import {Condition} from 'studies/typed/shared/useExperimentDesignState';

/**
 * Helper function that generates a unique destination unit-op instance key based on
 * method name, condition name, and replicate number.
 */
export function getDestinationUnitOpInstanceKey(
  methodName: string,
  conditionName: string,
  replicateNumber: number
): string {
  return `${methodName}-${conditionName}-${replicateNumber}`;
}

export function getDestinationPMEIIds(
  procedure: ExperimentProcedure | null,
  conditions: Array<Condition> | null
): Set<string> {
  if (procedure == null || conditions == null) {
    return new Set();
  }
  return new Set(
    procedure.methods.flatMap((method) =>
      conditions.flatMap((condition) =>
        [...Array(condition.numReplicates).keys()].map((i) =>
          getDestinationUnitOpInstanceKey(method.name, condition.name, i + 1)
        )
      )
    )
  );
}

export function getDestinationPMEIs(
  procedure: ExperimentProcedure | null,
  conditions: Array<Condition> | null
): Array<UnitOperationFields> {
  if (procedure == null || conditions == null) {
    return [];
  }
  return procedure.methods.flatMap((method, methodIndex) =>
    conditions.flatMap((condition, conditionIndex) =>
      [...Array(condition.numReplicates).keys()].map((replicateIndex) => ({
        pmeiId: getDestinationUnitOpInstanceKey(method.name, condition.name, replicateIndex + 1),
        procedureRunName: null,
        procedureRunId: procedure.id,
        procedureRunDisplayId: null, // Doesn't need a display ID assigned since it's not used for the destination procedure run
        procedureRunOrderKey: 0, // Only one run is being added
        methodName: method.name,
        templateMethodId: method.id,
        methodOrderKey: methodIndex,
        conditionName: condition.name,
        conditionOrderKey: conditionIndex,
        replicateNumber: replicateIndex + 1,
        procedureType: procedure.procedureType,
        status: null,
      }))
    )
  );
}

function getDestinationPMEIToTemplatePMDVIdMapping(
  destinationPMEIs: Array<UnitOperationFields> | null,
  procedure: ExperimentProcedure | null
): Record<string, string> {
  const destinationPMEIsToTemplatePMDVs: Record<string, string> = {};
  if (procedure != null && destinationPMEIs != null) {
    procedure.methods.forEach((method) => {
      destinationPMEIs.forEach((destinationPMEI) => {
        if (method.name === destinationPMEI.methodName) {
          const templatePMDVId = method.id;
          if (templatePMDVId != null) {
            destinationPMEIsToTemplatePMDVs[destinationPMEI.pmeiId] = templatePMDVId;
          }
        }
      });
    });
  }
  return destinationPMEIsToTemplatePMDVs;
}

type SourcePMEIIdsByTemplatePMDVId = Map<string, Array<string>>;
type SourcePMEIIdsByReplicateNumber = Map<number, SourcePMEIIdsByTemplatePMDVId>;

/**
 * Source PMEIs keyed by condition name -> replicate number -> (templateMethodVersionId -> source PMEI IDs).
 *
 * Keying by identity (rather than positional indices) lets callers look up the source PMEIs for an
 * already-serialized condition directly from the fields it carries (`name`, `replicateNumber`,
 * `templateMethodVersionId`).
 */
export type TemplatePmdvIdToSourcePMEIIdsByConditionNameAndReplicate = Map<
  string,
  SourcePMEIIdsByReplicateNumber
>;

/* Create a mapping of template PMDV ID to source PMEI IDs by condition name and replicate number.
 */
export function createSourcePMEIsMapping(
  newLinks: Array<UnitOpLinkEdge> | null,
  destinationPMEIs: Array<UnitOperationFields> | null,
  procedure: ExperimentProcedure | null,
  conditions: Array<Condition> | null
): TemplatePmdvIdToSourcePMEIIdsByConditionNameAndReplicate {
  if (newLinks == null || destinationPMEIs == null || procedure == null || conditions == null) {
    return new Map();
  }

  // Seed an empty Map for every condition + replicate so lookups by identity always resolve.
  const sourcePMEIIdsByConditionNameAndReplicate: TemplatePmdvIdToSourcePMEIIdsByConditionNameAndReplicate =
    new Map();
  conditions.forEach((condition) => {
    const byReplicate: SourcePMEIIdsByReplicateNumber = new Map();
    for (let replicateNumber = 1; replicateNumber <= condition.numReplicates; replicateNumber++) {
      byReplicate.set(replicateNumber, new Map());
    }
    sourcePMEIIdsByConditionNameAndReplicate.set(condition.name, byReplicate);
  });

  const destinationPMEIsToTemplatePMDVIds = getDestinationPMEIToTemplatePMDVIdMapping(
    destinationPMEIs,
    procedure
  );
  const destinationPMEIsById = new Map(
    destinationPMEIs.map((destinationPMEI) => [destinationPMEI.pmeiId, destinationPMEI])
  );

  newLinks.forEach((edge) => {
    const destinationPMEI = destinationPMEIsById.get(edge.destinationPMEIId);
    const templatePMDVId = destinationPMEIsToTemplatePMDVIds[edge.destinationPMEIId];
    if (destinationPMEI == null || templatePMDVId == null) {
      return;
    }
    const sourcePMEIIdsByTemplatePMDVId = sourcePMEIIdsByConditionNameAndReplicate
      .get(destinationPMEI.conditionName)
      ?.get(destinationPMEI.replicateNumber);
    assert(
      sourcePMEIIdsByTemplatePMDVId != null,
      'Expected empty map to be pre-seeded for every condition and replicate.'
    );

    const existingSourcePMEIIds = sourcePMEIIdsByTemplatePMDVId.get(templatePMDVId);
    if (existingSourcePMEIIds == null) {
      sourcePMEIIdsByTemplatePMDVId.set(templatePMDVId, [edge.sourcePMEIId]);
      return;
    }
    existingSourcePMEIIds.push(edge.sourcePMEIId);
  });

  return sourcePMEIIdsByConditionNameAndReplicate;
}
